import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useHistory, useParams } from 'react-router-dom'
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  FileCopy as FileCopyIcon,
} from '@material-ui/icons'
import {
  AlertTitle,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
} from '@mui/material'
import dayjs from 'dayjs'
import GET_JOB_EXECUTION from 'Apollo/Queries/GetJobExecution'
import RETRY_JOB from 'Apollo/Mutations/RetryJob'
import { AdminPageWrapper } from '../AdminComponents'
import {
  LoadingContainer,
  ActionsContainer,
  BackButton,
  SuccessAlert,
  ErrorAlert,
  InfoSection,
  SectionHeader,
  SectionTitle,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  JobIdContainer,
  JobId,
  StatusChip,
  CodeBlock,
  StackTraceBlock,
  ErrorSection,
  ErrorTitle,
  StackTraceLabel,
  RetryButton,
} from './JobDetail.styled'

interface JobExecution {
  id: string
  jobName: string
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED'
  payload: Record<string, unknown> | null
  result: Record<string, unknown> | null
  errorMessage: string | null
  errorStack: string | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  duration: number | null
  createdByUserId: number | null
  organizationId: number | null
  createdByUser: {
    id: number
    firstName: string
    lastName: string
  } | null
}

export const JobDetail: React.FC = () => {
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const [showRetryAlert, setShowRetryAlert] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery<{
    jobExecution: JobExecution
  }>(GET_JOB_EXECUTION, {
    variables: { id },
  })

  // Set polling interval based on job status
  React.useEffect(() => {
    if (data?.jobExecution?.status === 'RUNNING') {
      const interval = setInterval(() => {
        refetch()
      }, 2000)
      return () => clearInterval(interval)
    }
    return undefined
  }, [data?.jobExecution?.status, refetch])

  const [retryJob, { loading: retryLoading }] = useMutation(RETRY_JOB, {
    onCompleted: (retryData) => {
      if (retryData.retryJob.success) {
        setShowRetryAlert(true)
        setRetryError(null)
        // Navigate to the new job after a short delay
        setTimeout(() => {
          history.push(
            `/internal/self-serve/job-admin/${retryData.retryJob.newJobId}`
          )
        }, 1500)
      } else {
        setRetryError(retryData.retryJob.error)
      }
    },
    onError: (mutationError) => {
      setRetryError(mutationError.message)
    },
  })

  const job = data?.jobExecution

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <ScheduleIcon />
      case 'RUNNING':
        return <PlayArrowIcon />
      case 'COMPLETED':
        return <CheckCircleIcon />
      case 'FAILED':
        return <ErrorIcon />
      default:
        return undefined
    }
  }

  const getStatusColor = (
    status: string
  ): 'default' | 'info' | 'success' | 'error' => {
    switch (status) {
      case 'PENDING':
        return 'default'
      case 'RUNNING':
        return 'info'
      case 'COMPLETED':
        return 'success'
      case 'FAILED':
        return 'error'
      default:
        return 'default'
    }
  }

  const formatDuration = (ms: number | null) => {
    if (!ms) return '-'
    if (ms < 1000) return `${ms} milliseconds`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} seconds`
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)} minutes`
    return `${(ms / 3600000).toFixed(1)} hours`
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const handleRetry = () => {
    if (job) {
      retryJob({ variables: { jobId: job.id } })
    }
  }

  if (loading) {
    return (
      <AdminPageWrapper title='Job Details'>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </AdminPageWrapper>
    )
  }

  if (error || !job) {
    return (
      <AdminPageWrapper title='Job Details'>
        <ErrorAlert severity='error'>
          <AlertTitle>Error</AlertTitle>
          {error ? error.message : 'Job not found'}
        </ErrorAlert>
      </AdminPageWrapper>
    )
  }

  return (
    <AdminPageWrapper
      title='Job Details'
      subtitle={`Job ID: ${job.id}`}
      actions={
        <ActionsContainer>
          <BackButton
            variant='contained'
            size='small'
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/internal/self-serve/job-admin')}
          >
            Back to Jobs
          </BackButton>
          <IconButton onClick={() => refetch()} size='small'>
            <RefreshIcon />
          </IconButton>
        </ActionsContainer>
      }
    >
      {showRetryAlert && (
        <SuccessAlert severity='success'>
          <AlertTitle>Job Retried Successfully</AlertTitle>
          Redirecting to the new job...
        </SuccessAlert>
      )}

      {retryError && (
        <ErrorAlert severity='error'>
          <AlertTitle>Retry Failed</AlertTitle>
          {retryError}
        </ErrorAlert>
      )}

      <InfoSection>
        <SectionHeader>
          <SectionTitle variant='h6'>Job Information</SectionTitle>
          {job.status === 'FAILED' && (
            <RetryButton
              variant='contained'
              color='primary'
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              disabled={retryLoading}
            >
              {retryLoading ? 'Retrying...' : 'Retry Job'}
            </RetryButton>
          )}
        </SectionHeader>

        <InfoGrid>
          <InfoItem>
            <InfoLabel variant='subtitle2'>Job Name</InfoLabel>
            <InfoValue variant='body1'>{job.jobName}</InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel variant='subtitle2'>Status</InfoLabel>
            <StatusChip
              icon={getStatusIcon(job.status)}
              label={job.status}
              color={getStatusColor(job.status)}
              size='small'
            />
          </InfoItem>

          <InfoItem>
            <InfoLabel variant='subtitle2'>Created At</InfoLabel>
            <InfoValue variant='body1'>
              {dayjs(job.createdAt).format('MMMM D, YYYY h:mm A')}
            </InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel variant='subtitle2'>Duration</InfoLabel>
            <InfoValue variant='body1'>
              {formatDuration(job.duration)}
            </InfoValue>
          </InfoItem>

          {job.startedAt && (
            <InfoItem>
              <InfoLabel variant='subtitle2'>Started At</InfoLabel>
              <InfoValue variant='body1'>
                {dayjs(job.startedAt).format('MMMM D, YYYY h:mm A')}
              </InfoValue>
            </InfoItem>
          )}

          {job.completedAt && (
            <InfoItem>
              <InfoLabel variant='subtitle2'>Completed At</InfoLabel>
              <InfoValue variant='body1'>
                {dayjs(job.completedAt).format('MMMM D, YYYY h:mm A')}
              </InfoValue>
            </InfoItem>
          )}

          {job.createdByUser && (
            <InfoItem>
              <InfoLabel variant='subtitle2'>Created By</InfoLabel>
              <InfoValue variant='body1'>
                {job.createdByUser.firstName} {job.createdByUser.lastName}
              </InfoValue>
            </InfoItem>
          )}

          <InfoItem>
            <InfoLabel variant='subtitle2'>Job ID</InfoLabel>
            <JobIdContainer>
              <JobId variant='body1'>{job.id}</JobId>
              <Tooltip title='Copy ID'>
                <IconButton
                  size='small'
                  onClick={() => copyToClipboard(job.id)}
                >
                  <FileCopyIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </JobIdContainer>
          </InfoItem>
        </InfoGrid>
      </InfoSection>

      {job.payload && (
        <InfoSection>
          <SectionHeader>
            <SectionTitle variant='h6'>Payload</SectionTitle>
            <Tooltip title='Copy payload'>
              <IconButton
                size='small'
                onClick={() =>
                  copyToClipboard(JSON.stringify(job.payload, null, 2))
                }
              >
                <FileCopyIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </SectionHeader>
          <CodeBlock component='pre'>
            {JSON.stringify(job.payload, null, 2)}
          </CodeBlock>
        </InfoSection>
      )}

      {job.result && (
        <InfoSection>
          <SectionHeader>
            <SectionTitle variant='h6'>Result</SectionTitle>
            <Tooltip title='Copy result'>
              <IconButton
                size='small'
                onClick={() =>
                  copyToClipboard(JSON.stringify(job.result, null, 2))
                }
              >
                <FileCopyIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </SectionHeader>
          <CodeBlock component='pre'>
            {JSON.stringify(job.result, null, 2)}
          </CodeBlock>
        </InfoSection>
      )}

      {job.errorMessage && (
        <ErrorSection>
          <ErrorTitle variant='h6' gutterBottom>
            Error Details
          </ErrorTitle>
          <ErrorAlert severity='error'>
            <AlertTitle>Error Message</AlertTitle>
            {job.errorMessage}
          </ErrorAlert>
          {job.errorStack && (
            <>
              <Divider sx={{ my: 2 }} />
              <StackTraceLabel variant='subtitle2' gutterBottom>
                Stack Trace
              </StackTraceLabel>
              <StackTraceBlock component='pre'>
                {job.errorStack}
              </StackTraceBlock>
            </>
          )}
        </ErrorSection>
      )}
    </AdminPageWrapper>
  )
}
